import type {
  LearningMaterial,
  SiteLearningMaterialManifest,
  SiteLearningWorkspace,
  SiteLearningWorkspaceManifest,
} from '@common/domain/learning-workspace';

const MAX_MARKDOWN_BYTES = 262_144;
const MAX_PREAMBLE_BYTES = 4_096;
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isValidSlug(value: unknown): value is string {
  return typeof value === 'string' && SLUG_PATTERN.test(value);
}

function assertManifest(value: unknown): asserts value is SiteLearningWorkspaceManifest {
  if (!isRecord(value)) {
    throw new Error('Learning workspace manifest must be an object');
  }
  if (value.schemaVersion !== 1) {
    throw new Error('Learning workspace schemaVersion must be 1');
  }
  if (typeof value.enabled !== 'boolean') {
    throw new Error('Learning workspace enabled must be a boolean');
  }
  if (!isRecord(value.persona) || !isValidSlug(value.persona.slug)) {
    throw new Error('Learning workspace persona slug is invalid');
  }
  if (!Array.isArray(value.materials)) {
    throw new Error('Learning workspace materials must be an array');
  }

  const slugs = new Set<string>();
  const orders = new Set<number>();
  value.materials.forEach((material, index) => {
    assertMaterialManifest(material, index, slugs, orders);
  });

  if (value.enabled && !value.materials.some((material) => material.enabled)) {
    throw new Error('Enabled learning workspaces require an enabled material');
  }
}

function assertMaterialManifest(
  value: unknown,
  index: number,
  slugs: Set<string>,
  orders: Set<number>,
): asserts value is SiteLearningMaterialManifest {
  if (!isRecord(value)) {
    throw new Error(`Learning workspace material ${index} must be an object`);
  }
  if (!isValidSlug(value.slug)) {
    throw new Error(`Learning workspace material ${index} slug is invalid`);
  }
  if (typeof value.title !== 'string' || value.title.trim() === '') {
    throw new Error(`Learning workspace material ${index} title is blank`);
  }
  if (
    value.companionDescription !== undefined
    && (typeof value.companionDescription !== 'string' || value.companionDescription.trim() === '')
  ) {
    throw new Error(`Learning workspace material ${index} companion description is blank`);
  }
  if (typeof value.order !== 'number' || !Number.isInteger(value.order) || value.order <= 0) {
    throw new Error(`Learning workspace material ${index} order must be a positive integer`);
  }
  if (typeof value.enabled !== 'boolean') {
    throw new Error(`Learning workspace material ${index} enabled must be a boolean`);
  }
  if (slugs.has(value.slug)) {
    throw new Error(`Learning workspace material slug ${value.slug} is duplicated`);
  }
  if (orders.has(value.order)) {
    throw new Error(`Learning workspace material order ${value.order} is duplicated`);
  }

  slugs.add(value.slug);
  orders.add(value.order);
}

function assertContentMatchesManifest(
  manifest: SiteLearningWorkspaceManifest,
  content: Readonly<Record<string, string>>,
  label: string,
): void {
  const manifestSlugs = new Set(manifest.materials.map((material) => material.slug));
  const contentSlugs = new Set(Object.keys(content));

  if (
    manifestSlugs.size !== contentSlugs.size ||
    [...manifestSlugs].some((slug) => !contentSlugs.has(slug)) ||
    [...contentSlugs].some((slug) => !manifestSlugs.has(slug))
  ) {
    throw new Error(`Learning workspace ${label} keys must exactly match material slugs`);
  }
}

function normalizeMaterial(
  material: SiteLearningMaterialManifest,
  content: Readonly<Record<string, string>>,
  curriculumGuides: Readonly<Record<string, string>> | undefined,
  preambles: Readonly<Record<string, string>> | undefined,
): LearningMaterial {
  const markdown = content[material.slug];
  if (typeof markdown !== 'string' || markdown.trim() === '') {
    throw new Error(`Learning workspace Markdown for ${material.slug} is blank`);
  }
  if (new TextEncoder().encode(markdown).byteLength > MAX_MARKDOWN_BYTES) {
    throw new Error(`Learning workspace Markdown for ${material.slug} exceeds ${MAX_MARKDOWN_BYTES} bytes`);
  }

  const curriculumGuideMarkdown = curriculumGuides?.[material.slug];
  if (curriculumGuideMarkdown !== undefined) {
    if (curriculumGuideMarkdown.trim() === '') {
      throw new Error(`Learning workspace curriculum guide for ${material.slug} is blank`);
    }
    if (new TextEncoder().encode(curriculumGuideMarkdown).byteLength > MAX_MARKDOWN_BYTES) {
      throw new Error(`Learning workspace curriculum guide for ${material.slug} exceeds ${MAX_MARKDOWN_BYTES} bytes`);
    }
  }

  const preambleMarkdown = preambles?.[material.slug];
  if (preambleMarkdown !== undefined) {
    if (preambleMarkdown.trim() === '') {
      throw new Error(`Learning workspace preamble for ${material.slug} is blank`);
    }
    if (new TextEncoder().encode(preambleMarkdown).byteLength > MAX_PREAMBLE_BYTES) {
      throw new Error(`Learning workspace preamble for ${material.slug} exceeds ${MAX_PREAMBLE_BYTES} bytes`);
    }
  }

  return Object.freeze({
    ...material,
    contentFormat: 'markdown' as const,
    contentMarkdown: markdown,
    ...(curriculumGuideMarkdown === undefined ? {} : { curriculumGuideMarkdown }),
    ...(preambleMarkdown === undefined ? {} : { preambleMarkdown }),
  });
}

export function defineLearningWorkspace(
  manifest: unknown,
  content: Readonly<Record<string, string>>,
  curriculumGuides?: Readonly<Record<string, string>>,
  preambles?: Readonly<Record<string, string>>,
): SiteLearningWorkspace {
  assertManifest(manifest);
  assertContentMatchesManifest(manifest, content, 'content');
  if (curriculumGuides !== undefined) {
    assertContentMatchesManifest(manifest, curriculumGuides, 'curriculum guide');
  }
  if (preambles !== undefined) {
    assertContentMatchesManifest(manifest, preambles, 'preamble');
  }

  const materials = Object.freeze(
    manifest.materials
      .map((material) => normalizeMaterial(material, content, curriculumGuides, preambles))
      .sort((left, right) => left.order - right.order),
  );

  return Object.freeze({
    enabled: manifest.enabled,
    persona: Object.freeze({ ...manifest.persona }),
    materials,
  });
}
